import { useState, useRef, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Monitor } from 'lucide-react'
import { api } from '../lib/api'

interface TerminalLine {
  type: 'input' | 'output' | 'error' | 'system'
  text: string
}

function ColourText({ text }: { text: string }) {
  // Colourize status keywords inline
  const parts: { text: string; cls: string }[] = []
  const regex = /\b(CRITICAL|ELEVATED|OUTAGE|ERROR|DISRUPTED|DOWN|FAILED|operational|ok|low|connected|active|healthy|up|online|running|DEGRADED|GUARDED|STALE|WARNING|MAINTENANCE)\b/gi
  let lastIdx = 0
  let match: RegExpExecArray | null
  const r = new RegExp(regex)
  while ((match = r.exec(text)) !== null) {
    if (match.index > lastIdx) parts.push({ text: text.slice(lastIdx, match.index), cls: '' })
    const word = match[0].toUpperCase()
    const cls = ['CRITICAL','ELEVATED','OUTAGE','ERROR','DISRUPTED','DOWN','FAILED'].includes(word) ? 'text-rose-400 font-bold' :
                ['OPERATIONAL','OK','LOW','CONNECTED','ACTIVE','HEALTHY','UP','ONLINE','RUNNING'].includes(word) ? 'text-emerald font-bold' :
                'text-amber font-bold'
    parts.push({ text: match[0], cls })
    lastIdx = match.index + match[0].length
  }
  if (lastIdx < text.length) parts.push({ text: text.slice(lastIdx), cls: '' })
  if (parts.length <= 1 && !parts[0]?.cls) return <>{text}</>
  return <>{parts.map((p, i) => p.cls ? <span key={i} className={p.cls}>{p.text}</span> : <span key={i}>{p.text}</span>)}</>
}

const HELP_TEXT = `
TECHDASHBOARD TERMINAL v4.0
═══════════════════════════

── Intelligence feeds ─────────────────────────────────
  /search <query>         Search articles
  /entity <name>          Deep-dive on a company / tech
  /trends                 Trending entities across feeds
  /news [category]        Latest headlines
  /cloud                  Cloud provider status
  /cves                   Latest critical CVEs
  /threat                 Threat-level summary
  /threats                Live IOCs / C2s / advisories
  /outages                Recent cloud status changes
  /feeds                  Feed source health
  /db                     DB row counts

── Engineering ops ────────────────────────────────────
  /services               Service catalog (count by tier)
  /service <name>         Detail for one service
  /risks [open|manual]    Risk matrix summary
  /incidents              Internal Postmortem Log (MTTR, open)
  /decisions              Decision Log (ADRs)
  /health                 Service Catalog health scores
  /exec                   Executive Summary rollup
  /connectors             Connector list + last run

── Evergreen / releases ───────────────────────────────
  /eol                    Products ending soon (90d)
  /eol-product <slug>     Full cycle history
  /countdowns             Days-until / days-since clocks
  /patches                Patch Tuesday / Oracle CPU

── AI ─────────────────────────────────────────────────
  /models                 AI model leaderboard
  /cost <in>/<out>/M      LLM cost calc (tokens/million)
  /curate                 AI picks top stories now
  /summarise              AI executive summary
  /ask <question>         Free-form AI Q&A

── Frontier AI Intelligence ──────────────────────────
  /frontier-index         Directory of all 41 catalogues
  /frontier               81-org lab catalogue
  /frontier-globe         WebGL globe of lab HQs + arcs
  /timeline               Unified incidents/releases/lawsuits feed
  /editor                 Frontier Editor (admin CRUD)
  /notable                Epoch notable-models registry
  /rankings               5 leaderboards over Epoch data
  /pricing-frontier       $/Mtok pricing for 20 models
  /releases               Curated model-release timeline
  /papers                 HF Daily Papers (community-upvoted)
  /arxiv                  arXiv firehose (cs.AI / cs.CL / cs.LG)
  /benchmarks             Capability benchmark SOTAs
  /strategies             National AI strategies (Stargate / ARIA / FYP)
  /industry               19-tab industry watch (lawsuits, M&A, …)
  /knowledge              14-tab AI knowledge base
  /incidents-ai           120 dated AI safety incidents
  /regulation             16-jurisdiction regulation tracker
  /defence                Frontier Defence Lab (5 tracks)
  /vigilance              Mythos Vigilance (admin-gated)

── Watchlist + workflow ───────────────────────────────
  /watchlist              Your watched entities
  /watch <name>           Add to watchlist
  /unwatch <name>         Remove from watchlist
  /bookmark <title>       Save to bookmarks
  /note <title>|<body>    Save a note
  /wiki [query]           Search knowledge base
  /open <slug>            Open wiki article

── Delivery ───────────────────────────────────────────
  /briefing               Today's executive briefing
  /discord test           Test Discord webhook
  /discord briefing       Send briefing to Discord

── Navigation (jump to page) ──────────────────────────
  /cloud /cyber /warroom /world /trends /charts /news
  /innovation /startups /manda /energy /supply /hype
  /sentiment /competitors /watchlist /workspace /admin
  /exec /risk /svc /graph /pm /adr /eol /clock /conn
  /models /cost /wiki /help-panel
  /frontier-index /frontier /frontier-globe /timeline /editor
  /notable /rankings /pricing-frontier /releases /papers /arxiv
  /benchmarks /strategies /industry /knowledge /incidents-ai
  /regulation /defence /vigilance

── Meta ───────────────────────────────────────────────
  /help                   Show this help
  /cls   /clear           Clear screen
`.trim()

const NAV: Record<string, string> = {
  '/cloud': '/app/cloud', '/cyber': '/app/cyber', '/warroom': '/app/cyber-warroom',
  '/world': '/app/world', '/trends': '/app/trends', '/charts': '/app/trend-charts',
  '/watchlist': '/app/watchlist', '/news': '/app/tech-news', '/innovation': '/app/innovation',
  '/startups': '/app/startups', '/briefing': '/app/briefing', '/workspace': '/app/workspace',
  '/terminal': '/app/terminal', '/home': '/app', '/dashboard': '/app',
  '/hype': '/app/hype-cycle', '/competitors': '/app/competitors', '/sentiment': '/app/sentiment',
  '/energy': '/app/energy', '/supply': '/app/supply-chain', '/manda': '/app/manda',
  // New nav shortcuts for everything built this session
  '/exec': '/app/executive', '/executive': '/app/executive',
  '/risk': '/app/risk-matrix', '/risks-page': '/app/risk-matrix',
  '/svc': '/app/services', '/catalog': '/app/services',
  '/graph': '/app/service-graph',
  '/pm': '/app/postmortems', '/postmortem': '/app/postmortems', '/postmortems-page': '/app/postmortems',
  '/adr': '/app/decisions', '/decisions-page': '/app/decisions',
  '/eol-page': '/app/eol',
  '/clock': '/app/countdowns', '/countdown-page': '/app/countdowns',
  '/conn': '/app/connectors-manage',
  '/models': '/app/ai-models',
  '/cost': '/app/ai-costs',
  '/wiki-page': '/app/wiki',
  '/admin': '/app/admin',
  '/settings': '/app/settings',
  '/compliance': '/app/compliance', '/regulatory': '/app/regulatory',
  '/earnings': '/app/earnings', '/pricing': '/app/pricing',
  '/internet': '/app/internet-health', '/outage-page': '/app/outage-history',
  '/tech-radar': '/app/tech-radar', '/vendor-compare': '/app/vendor-comparison',
  '/ai': '/app/ai-insights', '/ai-adoption': '/app/ai-adoption',
  // ── Frontier-AI Intelligence shortcuts (built this session) ──
  '/frontier-index': '/app/frontier-index', '/frontier': '/app/frontier',
  '/frontier-globe': '/app/frontier-globe', '/labs': '/app/frontier',
  '/timeline': '/app/frontier-timeline', '/editor': '/app/frontier-editor',
  '/notable': '/app/notable-models', '/rankings': '/app/frontier-rankings',
  '/pricing-frontier': '/app/frontier-pricing',
  '/releases': '/app/model-releases', '/papers': '/app/trending-papers',
  '/arxiv': '/app/arxiv', '/benchmarks': '/app/benchmarks',
  '/strategies': '/app/national-strategies', '/national': '/app/national-strategies',
  '/industry': '/app/industry-watch', '/knowledge': '/app/knowledge-base',
  '/incidents-ai': '/app/ai-incidents', '/ai-incidents': '/app/ai-incidents',
  '/regulation': '/app/frontier-regulation', '/regulations': '/app/frontier-regulation',
  '/defence': '/app/frontier-defence', '/defense': '/app/frontier-defence',
  '/vigilance': '/app/vigilance', '/mythos': '/app/vigilance',
}

export default function TerminalPage() {
  const navigate = useNavigate()
  const [lines, setLines] = useState<TerminalLine[]>([
    { type: 'system', text: 'TechDashboard Terminal v3.0' },
    { type: 'system', text: 'Type "help" for available commands.' },
    { type: 'system', text: '' },
  ])
  const [input, setInput] = useState('')
  const [processing, setProcessing] = useState(false)
  const [history, setHistory] = useState<string[]>([])
  const [historyIdx, setHistoryIdx] = useState(-1)
  const bottomRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [lines])

  const addOutput = (text: string, type: TerminalLine['type'] = 'output') => {
    setLines(prev => [...prev, { type, text }])
  }

  const addLines = (texts: string[], type: TerminalLine['type'] = 'output') => {
    setLines(prev => [...prev, ...texts.map(text => ({ type, text }))])
  }

  const handleCommand = async (cmd: string) => {
    const trimmed = cmd.trim()
    if (!trimmed) return

    setHistory(prev => [trimmed, ...prev.slice(0, 50)])
    setHistoryIdx(-1)
    addOutput(`> ${trimmed}`, 'input')
    setProcessing(true)

    try {
      // Strip leading / for command processing
      const cleaned = trimmed.startsWith('/') ? trimmed.slice(1) : trimmed

      // Check navigation first
      const navKey = '/' + cleaned.split(' ')[0].toLowerCase()
      const dataCommands = new Set([
        'search', 'entity', 'watch', 'unwatch', 'cloud', 'cves', 'threat', 'threats',
        'trends', 'watchlist', 'feeds', 'db', 'briefing', 'discord', 'help', 'clear', 'cls',
        'services', 'svc-list', 'service', 'health', 'risks', 'risk', 'postmortems', 'pm',
        'incidents-internal', 'decisions', 'adr', 'eol', 'eol-product', 'countdowns', 'clocks',
        'patches', 'exec', 'executive', 'connectors', 'conn-list', 'models', 'cost',
        'wiki', 'open', 'read', 'help-panel', 'bookmark', 'note', 'curate', 'outages',
        'summarise', 'summarize', 'summary', 'ask',
      ])
      if (NAV[navKey] && !dataCommands.has(cleaned.split(' ')[0].toLowerCase())) {
        addOutput(`→ Navigating to ${navKey}`, 'system')
        setTimeout(() => navigate(NAV[navKey]), 300)
        setProcessing(false)
        return
      }

      const parts = cleaned.split(/\s+/)
      const command = parts[0].toLowerCase()
      const args = parts.slice(1).join(' ')

      switch (command) {
        case 'help':
          addLines(HELP_TEXT.split('\n'), 'system')
          break

        case 'cls':
          setLines([{ type: 'system', text: 'Screen cleared.' }])
          setProcessing(false)
          return

        case 'bookmark': {
          if (!args) { addOutput('Usage: /bookmark <article title>', 'error'); break }
          try {
            await api.post('/api/content/bookmarks', { article_title: args })
            addOutput(`Bookmarked: "${args}" ✓`, 'system')
          } catch { addOutput('Bookmark failed', 'error') }
          break
        }

        case 'note': {
          if (!args) { addOutput('Usage: /note <title> | <content>', 'error'); break }
          const [noteTitle, ...noteContent] = args.split('|')
          try {
            await api.post('/api/content/notes', { title: noteTitle.trim(), content: (noteContent.join('|') || '').trim() })
            addOutput(`Note saved: "${noteTitle.trim()}" ✓`, 'system')
          } catch { addOutput('Note save failed', 'error') }
          break
        }

        case 'curate': {
          addOutput('Asking AI to curate top stories...', 'system')
          try {
            const { data } = await api.post('/api/ai/curate')
            addOutput(`AI selected ${(data.curated || []).length} articles from ${data.total_reviewed}:`, 'system')
            for (const a of (data.curated || []).slice(0, 8)) {
              addOutput(`  [${a.source}] ${a.title?.slice(0, 70)}`)
              if (a.ai_reason) addOutput(`    → ${a.ai_reason}`, 'system')
            }
          } catch { addOutput('Curation failed', 'error') }
          break
        }

        case 'outages': {
          try {
            const { data } = await api.get('/api/outages/timeline', { params: { limit: 10 } })
            if ((data.events || []).length === 0) {
              addOutput('No outage events recorded yet.', 'system')
            } else {
              addOutput(`Recent cloud status changes (${data.total}):`, 'system')
              for (const e of data.events) {
                const icon = e.type === 'recovered' ? '✓' : '✗'
                addOutput(`  ${icon} ${e.provider}: ${e.previous_status} → ${e.status}  (${e.timestamp?.slice(0, 16)})`)
              }
            }
          } catch { addOutput('Outage history fetch failed', 'error') }
          break
        }

        case 'summarise':
        case 'summarize':
        case 'summary': {
          addOutput('Generating AI executive summary...', 'system')
          try {
            const { data } = await api.post('/api/ai/summarise')
            for (const line of (data.summary || '').split('\n')) addOutput(line)
          } catch { addOutput('Summary generation failed', 'error') }
          break
        }

        case 'ask': {
          if (!args) { addOutput('Usage: /ask <question>', 'error'); break }
          addOutput(`Asking AI: "${args}"...`, 'system')
          try {
            const { data } = await api.post('/api/ai/ask', { question: args })
            for (const line of (data.answer || '').split('\n')) addOutput(line)
          } catch { addOutput('AI question failed', 'error') }
          break
        }

        case 'clear':
          setLines([{ type: 'system', text: 'Terminal cleared.' }])
          break

        case 'search': {
          if (!args) { addOutput('Usage: search <query>', 'error'); break }
          const { data } = await api.get('/api/search', { params: { q: args, limit: 15 } })
          addOutput(`Found ${data.total} results for "${args}":`, 'system')
          for (const r of data.results || []) {
            addOutput(`  [${r.source}] ${r.title}`)
          }
          if (data.total === 0) addOutput('  No results found.', 'system')
          break
        }

        case 'entity': {
          if (!args) { addOutput('Usage: entity <name>', 'error'); break }
          const { data } = await api.get(`/api/trends/entity/${encodeURIComponent(args)}`)
          addOutput(`Entity: ${data.entity} — ${data.total_mentions} mentions`, 'system')
          if (data.sources?.length) {
            addOutput('  Sources:', 'system')
            for (const s of data.sources.slice(0, 10)) {
              addOutput(`    ${s.name}: ${s.count} articles`)
            }
          }
          if (data.articles?.length) {
            addOutput('  Recent articles:', 'system')
            for (const a of data.articles.slice(0, 5)) {
              addOutput(`    [${a.source}] ${a.title}`)
            }
          }
          break
        }

        case 'trends': {
          const { data } = await api.get('/api/trends/live')
          addOutput(`Trending (from ${data.total_articles_scanned} articles):`, 'system')
          addOutput('  COMPANIES:', 'system')
          for (const c of (data.trending_companies || []).slice(0, 10)) {
            addOutput(`    ${c.name.padEnd(25)} ${String(c.mentions).padStart(4)} mentions`)
          }
          addOutput('  TECHNOLOGIES:', 'system')
          for (const t of (data.trending_technologies || []).slice(0, 8)) {
            addOutput(`    ${t.name.padEnd(25)} ${String(t.mentions).padStart(4)} mentions`)
          }
          break
        }

        case 'cloud': {
          const { data } = await api.get('/api/cloud/status')
          addOutput(`Cloud Status (${data.providers?.length || 0} providers):`, 'system')
          for (const p of data.providers || []) {
            const icon = p.overall_status === 'operational' ? '●' : p.overall_status === 'outage' ? '✗' : '▲'
            const name = p.name?.replace('Amazon Web Services', 'AWS').replace('Google Cloud Platform', 'GCP')
            addOutput(`  ${icon} ${(name || p.slug).padEnd(22)} ${p.overall_status.toUpperCase()}`)
          }
          break
        }

        case 'cves': {
          const { data } = await api.get('/api/cyber/cves', { params: { limit: 10, severity: 'critical' } })
          addOutput(`Critical CVEs (${data.total}):`, 'system')
          for (const c of data.cves || []) {
            const wild = c.exploited_in_wild ? ' [EXPLOITED]' : ''
            addOutput(`  ${c.id} (CVSS ${c.cvss_score})${wild}`)
            addOutput(`    ${c.summary.slice(0, 120)}`)
          }
          if ((data.cves || []).length === 0) addOutput('  No critical CVEs found.', 'system')
          break
        }

        case 'threat': {
          const { data } = await api.get('/api/cyber/threat-level')
          addOutput(`Threat Level: ${data.level?.toUpperCase()}`, 'system')
          addOutput(`  Critical CVEs (7d): ${data.critical_cves_7d}`)
          addOutput(`  Exploited in Wild:  ${data.kev_additions_7d}`)
          addOutput(`  Ransomware-linked:  ${data.ransomware_linked}`)
          addOutput(`  ${data.summary}`)
          break
        }

        case 'feeds': {
          const { data } = await api.get('/api/system/feeds')
          addOutput(`Feed Sources: ${data.total_sources} total, ${data.active_sources} active, ${data.stale_sources} stale`, 'system')
          for (const f of (data.feeds || []).slice(0, 20)) {
            const icon = f.health === 'active' ? '●' : f.health === 'stale' ? '○' : '✗'
            addOutput(`  ${icon} ${f.source.padEnd(30)} ${String(f.last_24h).padStart(3)} (24h)  ${String(f.total).padStart(5)} total`)
          }
          break
        }

        case 'threats': {
          const { data } = await api.get('/api/threats/live', { params: { limit: 30 } })
          const stats = data.stats || {}
          addOutput(`Threat Intel: ${stats.sources_active}/${stats.sources_total} sources active`, 'system')
          addOutput(`  Advisories: ${stats.total_advisories} | IOCs: ${stats.total_iocs} | URLs: ${stats.total_malicious_urls} | C2s: ${stats.total_c2_servers}`, 'system')
          addOutput('', 'system')
          addOutput('  LATEST ADVISORIES:', 'system')
          for (const a of (data.advisories || []).slice(0, 8)) {
            const sev = a.severity === 'critical' ? 'CRITICAL' : a.severity === 'high' ? 'HIGH' : a.severity
            addOutput(`  [${sev.padEnd(8)}] [${a.source}] ${a.title.slice(0, 80)}`)
          }
          if ((data.iocs || []).length > 0) {
            addOutput('', 'system')
            addOutput('  LATEST IOCs:', 'system')
            for (const ioc of (data.iocs || []).slice(0, 5)) {
              addOutput(`  ${ioc.ioc_type.padEnd(12)} ${ioc.ioc.slice(0, 60).padEnd(62)} ${ioc.malware}`)
            }
          }
          break
        }

        case 'watchlist': {
          const { data } = await api.get('/api/watchlist')
          addOutput(`Watchlist (${data.total} entities):`, 'system')
          for (const e of data.entities || []) {
            addOutput(`  ${e.name.padEnd(22)} ${String(e.mentions_7d).padStart(4)} mentions  ${String(e.sources).padStart(3)} sources`)
          }
          break
        }

        case 'watch': {
          if (!args) { addOutput('Usage: watch <entity name>', 'error'); break }
          await api.post('/api/watchlist/add', { name: args })
          addOutput(`Added "${args}" to watchlist ✓`, 'system')
          break
        }

        case 'unwatch': {
          if (!args) { addOutput('Usage: unwatch <entity name>', 'error'); break }
          await api.delete('/api/watchlist/remove', { params: { name: args } })
          addOutput(`Removed "${args}" from watchlist ✓`, 'system')
          break
        }

        case 'db': {
          const { data } = await api.get('/api/system/db-stats')
          addOutput('Database Statistics:', 'system')
          for (const [table, count] of Object.entries(data.tables || {})) {
            addOutput(`  ${table.padEnd(20)} ${String(count).padStart(8)} rows`)
          }
          if (data.categories) {
            addOutput('  Categories:', 'system')
            for (const [cat, count] of Object.entries(data.categories || {})) {
              addOutput(`    ${cat.padEnd(18)} ${String(count).padStart(6)}`)
            }
          }
          break
        }

        case 'briefing': {
          const { data } = await api.get('/api/cyber/threat-level')
          const cloud = await api.get('/api/cloud/status')
          const trends = await api.get('/api/trends/live')

          addOutput('═══════════════════════════════════════════', 'system')
          addOutput(`  EXECUTIVE BRIEFING — ${new Date().toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}`, 'system')
          addOutput('═══════════════════════════════════════════', 'system')
          addOutput('')
          addOutput(`  THREAT LEVEL: ${data.level?.toUpperCase()}`, 'system')
          addOutput(`  Critical CVEs: ${data.critical_cves_7d} | Exploited: ${data.kev_additions_7d} | Ransomware: ${data.ransomware_linked}`)
          addOutput('')

          const issues = (cloud.data.providers || []).filter((p: any) => p.overall_status !== 'operational')
          if (issues.length === 0) {
            addOutput(`  CLOUD: All ${cloud.data.providers?.length || 0} providers operational ✓`, 'system')
          } else {
            addOutput(`  CLOUD: ${issues.length} provider(s) with issues`, 'error')
            for (const p of issues) addOutput(`    ${p.name} — ${p.overall_status.toUpperCase()}`)
          }
          addOutput('')

          addOutput('  TRENDING:', 'system')
          for (const c of (trends.data.trending_companies || []).slice(0, 5)) {
            addOutput(`    ${c.name} (${c.mentions} mentions)`)
          }
          addOutput('')
          addOutput('═══════════════════════════════════════════', 'system')
          break
        }

        case 'discord': {
          if (args === 'test') {
            const { data } = await api.post('/api/discord/test')
            addOutput(data.success ? 'Discord test message sent ✓' : 'Discord send failed ✗', data.success ? 'system' : 'error')
          } else if (args === 'briefing') {
            const { data } = await api.post('/api/discord/briefing')
            addOutput(data.success ? 'Briefing sent to Discord ✓' : 'Discord send failed ✗', data.success ? 'system' : 'error')
          } else {
            addOutput('Usage: discord test | discord briefing', 'error')
          }
          break
        }

        // ═══════ New commands (Engineering Ops + AI + Evergreen + Knowledge Base) ═══════

        case 'services':
        case 'svc-list': {
          const { data } = await api.get('/api/services')
          const byTier: Record<string, number> = {}
          for (const s of data.services || []) byTier[`T${s.tier}`] = (byTier[`T${s.tier}`] || 0) + 1
          addOutput(`Service Catalog — ${data.count} services`, 'system')
          addOutput(`  ${Object.entries(byTier).sort().map(([t, n]) => `${t}: ${n}`).join('  ·  ')}`)
          for (const s of (data.services || []).slice(0, 20)) {
            addOutput(`  T${s.tier} ${s.name.padEnd(28)} ${(s.kind || '').padEnd(10)} ${(s.cloud || '-').padEnd(12)} ${s.owner || '-'}`)
          }
          if (data.count > 20) addOutput(`  …${data.count - 20} more — /svc for full page`, 'system')
          break
        }

        case 'service': {
          if (!args) { addOutput('Usage: /service <name>', 'error'); break }
          const { data: list } = await api.get('/api/services')
          const s = (list.services || []).find((x: any) => x.name.toLowerCase() === args.toLowerCase() || x.name.toLowerCase().includes(args.toLowerCase()))
          if (!s) { addOutput(`Service "${args}" not found`, 'error'); break }
          addOutput(`${s.name}  (T${s.tier} · ${s.kind} · ${s.status})`, 'system')
          if (s.description) addOutput(`  ${s.description}`)
          addOutput(`  Owner: ${s.owner || '-'}  ·  Team: ${s.team || '-'}  ·  Cloud: ${s.cloud || '-'} ${s.region || ''}`.trim())
          if (s.sla) addOutput(`  SLA: ${s.sla}`)
          if (s.stack?.length) addOutput(`  Stack: ${s.stack.map((x: any) => `${x.name} ${x.version || ''}`.trim()).join(' · ')}`)
          if (s.dependencies?.length) addOutput(`  Depends on: ${s.dependencies.join(', ')}`)
          break
        }

        case 'health': {
          const { data } = await api.get('/api/services/health')
          addOutput(`Service Health — avg ${data.average}/100 (min ${data.min}, max ${data.max})`, 'system')
          addOutput(`  Healthy ${data.by_band.healthy || 0}  ·  Warning ${data.by_band.warning || 0}  ·  Concern ${data.by_band.concern || 0}  ·  Critical ${data.by_band.critical || 0}`)
          if (data.worst?.length) {
            addOutput('  Lowest scoring:', 'system')
            for (const w of data.worst.slice(0, 5)) {
              addOutput(`    ${w.score}/100 [${w.band}]  T${w.tier}  ${w.name}`)
            }
          }
          break
        }

        case 'risks':
        case 'risk': {
          const { data } = await api.get('/api/risks/matrix')
          addOutput(`Risk Matrix — ${data.total} risks`, 'system')
          addOutput(`  P5 Critical ${data.by_priority['5'] || 0}  ·  P4 Mitigate ${data.by_priority['4'] || 0}  ·  P3 Monitor ${data.by_priority['3'] || 0}  ·  P1-P2 Accept ${(data.by_priority['1'] || 0) + (data.by_priority['2'] || 0)}`)
          addOutput(`  By source: ${Object.entries(data.by_source).map(([k, v]) => `${k} ${v}`).join(' · ')}`)
          // Show top critical/high
          const top: any[] = []
          for (const cell of Object.values<any>(data.cells)) {
            if (cell.priority >= 4) top.push(...cell.risks)
          }
          top.sort((a, b) => b.priority - a.priority).slice(0, 8).forEach((r: any) => {
            addOutput(`  P${r.priority} [${r.source}] ${r.title.slice(0, 80)}`)
          })
          break
        }

        case 'incidents-internal':
        case 'postmortems':
        case 'pm': {
          const { data } = await api.get('/api/postmortems/stats')
          addOutput(`Postmortem Log — ${data.total} incidents (${data.open} open)`, 'system')
          addOutput(`  MTTR ${data.mttr_minutes ?? '?'}m  ·  MTTD ${data.mttd_minutes ?? '?'}m  ·  30d: ${data.last_30d}  ·  90d: ${data.last_90d}`)
          for (const [sev, n] of Object.entries(data.by_severity || {})) addOutput(`  ${sev.toUpperCase()}: ${n}`)
          if (data.last_resolved_at) addOutput(`  Last resolved: ${data.last_resolved_at.slice(0, 16).replace('T', ' ')} UTC`)
          break
        }

        case 'decisions':
        case 'adr': {
          const { data } = await api.get('/api/decisions/stats')
          addOutput(`Decision Log — ${data.total} ADRs  (${data.last_90d} in last 90d)`, 'system')
          for (const [status, n] of Object.entries(data.by_status || {})) addOutput(`  ${status}: ${n}`)
          if (data.latest?.length) {
            addOutput('  Latest:', 'system')
            for (const d of data.latest) {
              addOutput(`  ADR-${d.id} [${d.status}] ${d.title}`)
            }
          }
          break
        }

        case 'eol': {
          const { data } = await api.get('/api/eol/upcoming', { params: { days: 180 } })
          addOutput(`EOL within 180 days — ${data.count} cycles`, 'system')
          for (const c of (data.cycles || []).slice(0, 20)) {
            addOutput(`  ${String(c.days_to_eol).padStart(4)}d  ${c.product.padEnd(25)} ${c.cycle.padStart(10)}  eol ${c.eol}`)
          }
          break
        }

        case 'eol-product': {
          if (!args) { addOutput('Usage: /eol-product <slug> — e.g. /eol-product nodejs', 'error'); break }
          try {
            const { data } = await api.get(`/api/eol/product/${encodeURIComponent(args)}`)
            addOutput(`${data.name} (${data.cycles.length} cycles)`, 'system')
            for (const c of data.cycles.slice(0, 10)) {
              addOutput(`  ${c.cycle.padEnd(10)} ${c.state.padEnd(14)} released ${c.release_date || '-'} eol ${c.eol || '-'} latest ${c.latest || '-'}`)
            }
          } catch { addOutput(`Product "${args}" not in catalog`, 'error') }
          break
        }

        case 'countdowns':
        case 'clocks': {
          const { data } = await api.get('/api/countdowns')
          addOutput(`Countdown Board — ${data.count} clocks  (${data.by_severity.bad} bad · ${data.by_severity.warn} warn · ${data.by_severity.good} good)`, 'system')
          for (const c of data.clocks || []) {
            const val = c.display || `${c.days}d`
            const marker = c.severity === 'bad' ? '[BAD] ' : c.severity === 'warn' ? '[WARN]' : '[OK]  '
            addOutput(`  ${marker} ${c.kind === 'until' ? 'until' : 'since'} ${val.padEnd(8)} ${c.label}`)
          }
          break
        }

        case 'patches': {
          const { data } = await api.get('/api/countdowns')
          const patch = (data.clocks || []).filter((c: any) => c.id.includes('patch') || c.id.includes('oracle'))
          for (const p of patch) addOutput(`${p.days.toString().padStart(3)}d until ${p.label}  (${p.target_date.slice(0, 10)})`, 'system')
          break
        }

        case 'exec':
        case 'executive': {
          const { data } = await api.get('/api/executive-summary')
          addOutput(`Executive Summary — ${data.count} connectors  (${data.stale_connectors} stale)`, 'system')
          addOutput(`  Healthy ${data.health_counts.healthy}  ·  Degraded ${data.health_counts.degraded}  ·  Outage ${data.health_counts.outage}  ·  Unknown ${data.health_counts.unknown}`)
          const r = data.rollup
          addOutput(`  Open alerts ${r.alerts_open} (${r.alerts_critical} critical)  ·  Open incidents ${r.incidents_open}  ·  Licences ${r.licences_used}/${r.licences_total}  ·  Spend MTD $${(r.cost_mtd || 0).toFixed(0)}`)
          for (const t of (data.tiles || []).slice(0, 15)) {
            const headline = (t.summary?.headline || '').slice(0, 60)
            const status = t.last_status === 'ok' ? 'OK' : t.last_status === 'error' ? 'ERR' : '--'
            addOutput(`  [${status}] ${t.name.padEnd(28)} ${headline}`)
          }
          break
        }

        case 'connectors':
        case 'conn-list': {
          const { data } = await api.get('/api/connectors')
          addOutput(`Connectors — ${data.count} configured`, 'system')
          for (const c of data.connectors || []) {
            const age = c.last_run_at ? Math.round((Date.now() - new Date(c.last_run_at).getTime()) / 60000) + 'm' : 'never'
            addOutput(`  [${c.last_status}] ${c.name.padEnd(30)} kind=${c.kind.padEnd(15)} last=${age}`)
          }
          break
        }

        case 'models': {
          const { data } = await api.get('/api/ai-models', { params: { sort: 'composite' } })
          addOutput(`AI Model Leaderboard — ${data.count} models`, 'system')
          for (const m of (data.models || []).slice(0, 10)) {
            addOutput(`  ${m.composite.toFixed(1).padStart(5)}  ${m.name.padEnd(30)} ${m.provider.padEnd(12)} $${m.price_input}/${m.price_output}  ${(m.context_window / 1000).toFixed(0)}K ctx`)
          }
          break
        }

        case 'cost': {
          if (!args) { addOutput('Usage: /cost <input_M> <output_M>   e.g. /cost 100 20', 'error'); break }
          const nums = args.split(/\s+/).map(parseFloat)
          if (nums.length < 2 || nums.some(isNaN)) { addOutput('Usage: /cost <input_M> <output_M>', 'error'); break }
          const [inM, outM] = nums
          const { data } = await api.get('/api/ai-models', { params: { sort: 'price' } })
          addOutput(`LLM monthly cost for ${inM}M input + ${outM}M output tokens:`, 'system')
          const rows = (data.models || []).map((m: any) => ({ name: m.name, cost: inM * m.price_input + outM * m.price_output, composite: m.composite, provider: m.provider }))
          rows.sort((a: any, b: any) => a.cost - b.cost)
          for (const r of rows.slice(0, 10)) {
            addOutput(`  $${r.cost.toFixed(0).padStart(6)}/mo  ${r.composite.toFixed(1).padStart(5)} comp  ${r.name.padEnd(30)} ${r.provider}`)
          }
          break
        }

        case 'wiki': {
          const { data } = await api.get('/api/content/wiki')
          const pages = data.pages || []
          const filtered = args ? pages.filter((p: any) =>
            p.title.toLowerCase().includes(args.toLowerCase()) ||
            p.slug.toLowerCase().includes(args.toLowerCase()) ||
            p.category.toLowerCase().includes(args.toLowerCase())
          ) : pages
          addOutput(`Knowledge Base — ${filtered.length}${args ? ` matching "${args}"` : ''} of ${pages.length} pages`, 'system')
          const byCat: Record<string, any[]> = {}
          for (const p of filtered) (byCat[p.category] ||= []).push(p)
          for (const [cat, items] of Object.entries(byCat).sort()) {
            addOutput(`  [${cat}]`, 'system')
            for (const p of items) addOutput(`    ${p.slug.padEnd(28)} ${p.title}`)
          }
          addOutput('  Use /open <slug> to read a page.', 'system')
          break
        }

        case 'open':
        case 'read': {
          if (!args) { addOutput('Usage: /open <slug>   e.g. /open executive-summary', 'error'); break }
          try {
            const { data } = await api.get(`/api/content/wiki/${encodeURIComponent(args)}`)
            addOutput(`━━━ ${data.title}  [${data.category}] ━━━`, 'system')
            const lines = (data.content || '').split('\n')
            for (const line of lines.slice(0, 80)) addOutput(line || ' ')
            if (lines.length > 80) addOutput(`… +${lines.length - 80} more lines — open the wiki to read in full`, 'system')
          } catch { addOutput(`Page "${args}" not found — try /wiki to list`, 'error') }
          break
        }

        case 'help-panel': {
          // Keyboard access to the help panel
          document.querySelector<HTMLButtonElement>('button[aria-label="Open help / knowledge base"]')?.click()
          break
        }

        default:
          addOutput(`Unknown command: ${command}. Type "help" for available commands.`, 'error')
      }
    } catch (err: any) {
      addOutput(`Error: ${err.message || 'Command failed'}`, 'error')
    } finally {
      setProcessing(false)
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !processing) {
      handleCommand(input)
      setInput('')
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      if (history.length > 0) {
        const idx = Math.min(historyIdx + 1, history.length - 1)
        setHistoryIdx(idx)
        setInput(history[idx])
      }
    } else if (e.key === 'ArrowDown') {
      e.preventDefault()
      if (historyIdx > 0) {
        setHistoryIdx(historyIdx - 1)
        setInput(history[historyIdx - 1])
      } else {
        setHistoryIdx(-1)
        setInput('')
      }
    }
  }

  return (
    <div className="animate-fade-in">
      <div className="flex items-center gap-2 mb-3">
        <Monitor size={20} className="text-emerald" />
        <h1 className="text-xl font-bold text-text-primary">Terminal</h1>
        <span className="text-[10px] text-text-muted ml-auto">Type /help for commands</span>
      </div>

      <div
        className="crt-screen rounded-xl border border-emerald/20 bg-[#0a0a0a] p-4 font-mono text-sm overflow-hidden"
        style={{ height: 'calc(100vh - 160px)' }}
        onClick={() => inputRef.current?.focus()}
      >
        <div className="crt-scanlines" />
        <div className="overflow-y-auto h-full pb-8 crt-scrollbar" style={{ scrollbarWidth: 'thin' }}>
          {lines.map((line, i) => (
            <div key={i} className={
              line.type === 'input' ? 'text-amber brightness-125' :
              line.type === 'error' ? 'text-rose-400' :
              line.type === 'system' ? 'text-cyan-400' :
              'text-emerald/80'
            } style={{ minHeight: '1.3em', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
              <ColourText text={line.text} />
            </div>
          ))}
          <div ref={bottomRef} />

          {/* Input line */}
          <div className="flex items-center gap-1 mt-1">
            <span className="text-emerald brightness-150">{'>'}</span>
            <input
              ref={inputRef}
              type="text"
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              disabled={processing}
              className="flex-1 bg-transparent text-emerald border-none outline-none font-mono text-sm caret-emerald"
              autoFocus
              autoComplete="off"
              spellCheck={false}
            />
            {processing && <span className="text-amber text-xs animate-pulse">processing...</span>}
          </div>
        </div>
      </div>
    </div>
  )
}
